class ProductTabs extends HTMLElement {
  /** @type {HTMLElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /** @type {HTMLElement[]} */
  get panels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]'));
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#wrapTabs();

    this.tabs.forEach((tab, index) => {
      tab.addEventListener(
        "click",
        () => this.#activateTab(index, { focus: false }),
        { signal },
      );
      tab.addEventListener("keydown", this.#handleKeyDown, { signal });
    });

    this.#activateTab(0, { focus: false });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Groups the tab buttons into a single scrollable `role="tablist"` wrapper,
   * so they can scroll horizontally as a unit on narrow viewports without
   * affecting the layout of the tab panels.
   */
  #wrapTabs() {
    const tabs = this.tabs;

    if (tabs.length === 0 || this.querySelector(".product-tabs__tablist"))
      return;

    const tablist = document.createElement("div");
    tablist.className = "product-tabs__tablist";
    tablist.setAttribute("role", "tablist");

    tabs[0].before(tablist);
    tabs.forEach((tab) => tablist.appendChild(tab));
  }

  /**
   * Activates the tab at the given index, optionally moving focus to it.
   * @param {number} index - The index of the tab to activate.
   * @param {{ focus?: boolean }} [options] - Whether to focus the tab after activating it.
   */
  #activateTab(index, { focus = true } = {}) {
    const { tabs, panels } = this;
    const tab = tabs[index];
    const panel = panels[index];

    if (!tab || !panel) return;

    tabs.forEach((otherTab, otherIndex) => {
      const isActive = otherIndex === index;

      otherTab.setAttribute("aria-selected", isActive ? "true" : "false");
      otherTab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((otherPanel, otherIndex) => {
      otherPanel.hidden = otherIndex !== index;
    });

    if (focus) tab.focus();
  }

  /**
   * Handles arrow-key navigation between tabs.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    const { tabs } = this;
    const currentIndex = tabs.indexOf(
      /** @type {HTMLElement} */ (event.target),
    );

    if (currentIndex === -1) return;

    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.#activateTab((currentIndex + 1) % tabs.length);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.#activateTab((currentIndex - 1 + tabs.length) % tabs.length);
    }
  };
}

if (!customElements.get("product-tabs")) {
  customElements.define("product-tabs", ProductTabs);
}
